import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Checkbox } from "./ui/checkbox";
import { Label } from "./ui/label";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "../lib/api";

export function AddTagDialog({ capture, tags, open, onOpenChange, onTagged, onCreateTag }) {
  const { t } = useTranslation();
  const [selected, setSelected] = useState([]);
  const [newTagName, setNewTagName] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (capture) setSelected(capture.tags || []);
  }, [capture]);

  if (!capture) return null;

  const toggle = (tagId) => {
    setSelected((prev) => (prev.includes(tagId) ? prev.filter((id) => id !== tagId) : [...prev, tagId]));
  };

  const handleCreateAndSelect = async () => {
    if (!newTagName.trim()) return;
    try {
      const { data } = await api.post("/tags", { name: newTagName });
      onCreateTag(data);
      setSelected((prev) => [...prev, data.id]);
      setNewTagName("");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const { data } = await api.patch(`/captures/${capture.id}`, { tags: selected });
      onTagged(data);
      onOpenChange(false);
      toast.success(t("tagDialog.taggedSuccess"));
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-none max-w-sm" data-testid="add-tag-dialog">
        <DialogHeader>
          <DialogTitle className="font-display">{t("tagDialog.addTitle")}</DialogTitle>
          <DialogDescription>{t("tagDialog.addDescription")}</DialogDescription>
        </DialogHeader>
        <div className="space-y-2 max-h-48 overflow-y-auto">
          {tags.length === 0 && <p className="text-sm text-muted-foreground">{t("tagDialog.noTagsYet")}</p>}
          {tags.map((tag) => (
            <label key={tag.id} className="flex items-center gap-2 text-sm cursor-pointer" data-testid={`tag-option-${tag.id}`}>
              <Checkbox checked={selected.includes(tag.id)} onCheckedChange={() => toggle(tag.id)} />
              {tag.name}
            </label>
          ))}
        </div>
        <div className="flex gap-2 items-end border-t border-border pt-4">
          <div className="flex-1 space-y-1">
            <Label htmlFor="new-tag-inline">{t("tagDialog.newTagTitle")}</Label>
            <Input id="new-tag-inline" data-testid="add-tag-new-input" value={newTagName} onChange={(e) => setNewTagName(e.target.value)} placeholder={t("tagDialog.newTagPlaceholder")} className="rounded-none" />
          </div>
          <Button type="button" variant="outline" data-testid="add-tag-create-button" onClick={handleCreateAndSelect} className="rounded-none">
            {t("common.create")}
          </Button>
        </div>
        <Button data-testid="add-tag-save-button" disabled={saving} onClick={handleSave} className="w-full rounded-none">
          {saving ? t("common.saving") : t("common.save")}
        </Button>
      </DialogContent>
    </Dialog>
  );
}
